/**
 * The crop editor's geometry, kept apart from the UI so it is unit tested.
 *
 * The mask (the circle or rounded square the user sees) is a window of
 * `mask` CSS pixels in the middle of the stage. The image is drawn centred
 * on the mask, scaled by `baseScale * zoom` (zoom 1: the image just covers
 * the mask), and moved by (x, y) CSS pixels. The image always covers the
 * whole mask: pans and zooms are clamped so no empty corner shows, as in
 * Discord's "Edit image".
 */
import { MAX_ZOOM, type Size } from './plan';

export interface CropState {
  zoom: number;
  /** The image centre's offset from the mask centre, in CSS pixels. */
  x: number;
  y: number;
}

export interface CropGeometry {
  /** The image being cropped, in its own pixels. */
  image: Size;
  /** The mask, in CSS pixels. */
  mask: Size;
  maxZoom?: number;
}

export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

export const INITIAL_CROP: CropState = { zoom: 1, x: 0, y: 0 };

/** Image pixels to CSS pixels at zoom 1: the image just covers the mask. */
export function baseScale(g: CropGeometry): number {
  return Math.max(g.mask.width / g.image.width, g.mask.height / g.image.height);
}

export function scaleOf(g: CropGeometry, s: CropState): number {
  return baseScale(g) * s.zoom;
}

const clampN = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Keeps the zoom in range and the image covering the mask. */
export function clampCrop(g: CropGeometry, s: CropState): CropState {
  const zoom = clampN(Number.isFinite(s.zoom) ? s.zoom : 1, 1, g.maxZoom ?? MAX_ZOOM);
  const k = baseScale(g) * zoom;
  const mx = Math.max(0, (g.image.width * k - g.mask.width) / 2);
  const my = Math.max(0, (g.image.height * k - g.mask.height) / 2);
  return { zoom, x: clampN(s.x, -mx, mx) || 0, y: clampN(s.y, -my, my) || 0 };
}

export function panBy(g: CropGeometry, s: CropState, dx: number, dy: number): CropState {
  return clampCrop(g, { ...s, x: s.x + dx, y: s.y + dy });
}

/**
 * Zooms to `zoom` keeping the image point under `at` (CSS pixels from the
 * mask centre; the centre by default) where it is: a wheel or pinch zooms
 * around the pointer, the slider around the middle.
 */
export function zoomTo(
  g: CropGeometry,
  s: CropState,
  zoom: number,
  at: { x: number; y: number } = { x: 0, y: 0 },
): CropState {
  const next = clampN(zoom, 1, g.maxZoom ?? MAX_ZOOM);
  const r = next / s.zoom;
  return clampCrop(g, { zoom: next, x: at.x - (at.x - s.x) * r, y: at.y - (at.y - s.y) * r });
}

/** The part of the image inside the mask, in image pixels. */
export function cropRect(g: CropGeometry, s: CropState): CropRect {
  const c = clampCrop(g, s);
  const k = scaleOf(g, c);
  const sw = Math.min(g.image.width, g.mask.width / k);
  const sh = Math.min(g.image.height, g.mask.height / k);
  const cx = g.image.width / 2 - c.x / k;
  const cy = g.image.height / 2 - c.y / k;
  return {
    sx: clampN(cx - sw / 2, 0, g.image.width - sw),
    sy: clampN(cy - sh / 2, 0, g.image.height - sh),
    sw,
    sh,
  };
}

/** Where to draw the image on the stage: its top-left corner and size, in CSS pixels. */
export function drawRect(
  g: CropGeometry,
  s: CropState,
  stage: Size,
): { dx: number; dy: number; dw: number; dh: number } {
  const c = clampCrop(g, s);
  const k = scaleOf(g, c);
  const dw = g.image.width * k;
  const dh = g.image.height * k;
  return { dx: stage.width / 2 + c.x - dw / 2, dy: stage.height / 2 + c.y - dh / 2, dw, dh };
}

/** A mask of `aspect` (width / height) that fits `stage` with `pad` CSS pixels around it. */
export function maskSize(stage: Size, aspect: number, pad: number): Size {
  const w = Math.max(1, stage.width - pad * 2);
  const h = Math.max(1, stage.height - pad * 2);
  return w / h > aspect ? { width: h * aspect, height: h } : { width: w, height: w / aspect };
}
